import React from 'react';
import { styled } from '@compiled/react';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import type { CardId } from '../../../../model/types';
import { DividerStatistics } from '../../divider/divider-statistics';

type Props = {
	edge: Edge;
	droppingOverCardId: CardId;
	droppingOverCardListId: string;
	dropStatisticsCardListId?: string;
};

export function DropIndicator({
	edge,
	droppingOverCardId,
	droppingOverCardListId,
	dropStatisticsCardListId,
}: Props) {
	return (
		<DropLine edge={edge}>
			{dropStatisticsCardListId && (
				<InformationPill>
					<DividerStatistics
						dividerCardListId={dropStatisticsCardListId}
						isDragIndicator
						edge={edge === 'top' ? 'top' : 'bottom'}
						droppingOverCardListId={droppingOverCardListId}
						droppingOverCardId={droppingOverCardId}
					/>
				</InformationPill>
			)}
		</DropLine>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropLine = styled.div<{ edge: Edge }>({
	position: 'absolute',
	// Design tokens are in use here, but the linter doesn't recognize it
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	top: (props) =>
		props.edge === 'top'
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
				() =>
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					ff('jsw-tanuki-fit-and-finish_rhzmf')
						? token('space.negative.025', '-2px')
						: token('space.0', '0px')
			: undefined,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	bottom: (props) => (props.edge === 'bottom' ? token('space.0', '0px') : undefined),
	left: 0,
	width: '100%',
	height: '2px',
	zIndex: 1,
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.icon.brand', colors.B300),
	pointerEvents: 'none',
	'&::before': {
		content: "''",
		display: 'flex',
		height: 0,
		width: 0,
		alignSelf: 'center',
		marginLeft: token('space.negative.050', '-4px'),
		borderTop: '4px solid transparent',
		borderBottom: '4px solid transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderLeft: `8px solid ${token('color.border.brand', colors.B300)}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InformationPill = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.icon.brand', colors.B300),
	borderRadius: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: `${token('space.050', '4px')} ${token('space.100', '8px')}`,
});
